import { DOCUMENT } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationExtras, Params, Router } from '@angular/router';

import { environment } from '@env/environment';
import { LOCATION, ReferralParams, UtmParams } from '@personal-area/common';

import { I18nService } from '@personal-area/global/i18n';
import { CpaData, RefLink } from '@personal-area/global/registration';
import { Role, Type, userURLs } from '@personal-area/global/users/models';

import { LOCAL_STORAGE, LocalStorageService, SESSION_STORAGE, UniversalStorageService } from '@personal-area/storage';
import { Md5 } from 'ts-md5';
import { REFERRAL_QUERY_PARAMS } from '../constants/referral-query-params';
import { ReferralComposingService } from './referral-composing.service';
import { ReferralStorageService } from './referral-storage.service';

const retargetingCookie = 'rtrg';
const firstVisitCookie = 'first_visit_date';
const cpaNetworkCookie = 'cpa_network';
const cpaMediumCookie = 'cpa_medium';
const cpaUidCookie = 'cpa_uid';
const cpaUrlCookie = 'cpa_url';

export const utmDataKey = 'fbs:utm';
export const refLinksDataKey = 'fbs:ref';

export const partnerRefKey = 'ppu';
export const referrerNameKey = 'referrerName';
export const referrerName = 'tp_partner';

/**
 * TODO провести рефакторинг избавится по возможности от кук. Структурировать константы.
 * Проблемы:
 * 1. Нет метода по поиску через key_word
 * 2. Нет метода обновления partnerId
 * 3. Нет метода обновления referer_black_list
 * 4. Запрос в старое API UserSetRetargetingRequest
 * 5. Поиск значений в куках Yii и куках обычных
 */
@Injectable({ providedIn: 'root' })
export class ReferralService {
  private readonly _cookieService = inject(UniversalStorageService);
  private readonly _localStorageService = inject(LocalStorageService);
  private readonly _router = inject(Router);
  private readonly _i18nService = inject(I18nService);
  private readonly _referralStorage = inject(ReferralStorageService);
  private readonly _referralComposing = inject(ReferralComposingService);
  private readonly _document = inject(DOCUMENT);
  private readonly _location = inject(LOCATION);
  private readonly _localStorage = inject(LOCAL_STORAGE);
  private readonly _sessionStorage = inject(SESSION_STORAGE);

  private readonly _mediumMarkers = [
    'cpc',
    'cpm',
    'cpa',
    'email',
    'push',
    'banner',
    'newsletter',
    'cpv',
  ];

  private readonly _headersMap = [
    ['X-User-Utm-Campaign', 'utm_campaign'],
    ['X-User-Utm-Source', 'utm_source'],
    ['X-User-Utm-Medium', 'utm_medium'],
    ['X-User-Utm-Term', 'utm_term'],
    ['X-User-Utm-Content', 'utm_content'],
    ['X-User-Google-Gclid', 'gclid'],
    ['X-User-Google-Dclid', 'dclid'],
  ];
  private _isProcessedUtm = false;

  private readonly _referral = environment.referral;
  private _referralLinks: RefLink[] = [];

  public init(): void {
    this._referralStorage.migrateLegacyReferralData();
  }

  public saveParams(isLoggedId: boolean, params: Params): void {
    if (!this.checkRetargeting(isLoggedId, params as UtmParams)
      && !this._checkCpa(this._document.referrer, params as UtmParams)
    ) {
      this._checkAndSetPartnerRef(params as ReferralParams);
    }

    this._processUtmParams(params as UtmParams, !isLoggedId && !this._isProcessedUtm);
    if (!isLoggedId) {
      this._processReferralLinks(params);
    }

    this._setPartnerReferrerName(params as ReferralParams);

    // несмотря на то, что параметры мы должны сохранить 1 раз на старте, в init() эти сохранения добавлять нельзя
    // saveParams вызывается внутри подписки на изменение queryParams, а init() может быть вызван до выставления параметров в роутере
    const urlData = this._referralComposing.getComposedUrlReferralData(this._i18nService.languageA2);
    if (urlData) {
      this._referralStorage.save(urlData);
    } else {
      const storedData = this._referralComposing.getComposedStoredReferralData(this._i18nService.languageA2);
      if (storedData) {
        this._referralStorage.save(storedData);
      }
    }
  }

  private _setPartnerReferrerName(params: ReferralParams): void {
    if (!params.referrerName) {
      return;
    }

    this._localStorageService.setItem(referrerNameKey, params.referrerName, environment.partnerCookieExpire, this._localStorage);
  }

  private _processReferralLinks(params: Params): void {
    Object.keys(params).forEach(key => {
      if (key.startsWith('fbsref_')) {
        this._referralLinks = this._referralLinks
          .filter(l => l.name !== key);
        this._referralLinks.push({
          name: key,
          value: params[key].toString(),
        });
      }
    });
    if (this._referralLinks.length > 0) {
      this._localStorageService.setItem<RefLink[]>(refLinksDataKey, this._referralLinks, 0, this._localStorage);
    }
  }

  private _processUtmParams(params: UtmParams, write: boolean): void {
    if (!write || !params || Object.keys(params).length === 0) {
      return;
    }

    this._isProcessedUtm = true;
    const utmParams: Partial<UtmParams> = {};

    this._headersMap.forEach(([k, v]) => {
      if (params[v]) {
        utmParams[v] = params[v];
      }
    });

    if (utmParams && Object.keys(utmParams).length > 0) {
      this._localStorageService.setItem<Partial<UtmParams>>(utmDataKey, utmParams, 0, this._localStorage);
    }
  }

  public get referralLinks(): RefLink[] | null {
    try {
      return this._localStorageService.getItem<RefLink[]>(refLinksDataKey, this._localStorage);
    } catch (e) {
      return null;
    }
  }

  public utmParamHeaders(): HttpHeaders {
    let utmParams: UtmParams | null = null;
    try {
      utmParams = this._localStorageService.getItem<UtmParams>(utmDataKey, this._localStorage);
    } catch (e) {
    }

    if (!utmParams) {
      return new HttpHeaders();
    }

    if (Object.keys(utmParams).length > 0) {
      this._localStorageService.setItem<UtmParams>(utmDataKey, utmParams, 0, this._localStorage);
    }

    const headers = this._headersMap.reduce(
      (acc, [k, v]) => utmParams[v] ? { ...acc, [k]: utmParams[v] } : acc,
      <Record<string, string>>{},
    );

    return new HttpHeaders(headers);
  }

  public clearUtmParams(saveForSession: boolean): void {
    if (saveForSession) {
      if (this._localStorageService.getItem(utmDataKey, this._localStorage)) {
        this._localStorageService.setItem(utmDataKey, this._localStorage.getItem(utmDataKey), 0, this._sessionStorage);
      }
    }
    this._localStorageService.removeItem(utmDataKey, this._localStorage);
    this._localStorageService.removeItem(refLinksDataKey, this._localStorage);
  }

  public utmParams(restoreFromSession: boolean | null = null): UtmParams {
    return (
      this._localStorageService.getItem(utmDataKey, this._localStorage) ||
      (restoreFromSession && this._localStorageService.getItem(utmDataKey, this._sessionStorage))
    ) as UtmParams;
  }

  /**
   * @returns {CpaData}
   */
  public getCpaData(): CpaData | undefined {
    if (this._localStorageService.check(cpaNetworkCookie, this._localStorage)) {
      return new CpaData(
        this._localStorageService.getItem(cpaNetworkCookie, this._localStorage) || '',
        this._localStorageService.getItem(cpaUidCookie, this._localStorage) || '',
        this._localStorageService.getItem(cpaUrlCookie, this._localStorage) || '',
      );
    }

    return undefined;
  }

  /**
   * @param {string} name
   * @param {string} salt
   * @param {string} email
   * @returns {boolean}
   */
  public isEmailConfirmedOnLanding(name: string, salt: string, email: string): boolean {
    if (this._cookieService.check(name)) {
      return Md5.hashStr(salt + email) === this._cookieService.getItem(name);
    }
    return false;
  }

  public getPartnerReferrer(): string {
    return this._document.referrer;
  }

  /**
   * @returns {Date}
   */
  public getFirstVisitTime(): Date {
    let date = Date.now() / 1000;

    if (!this._localStorageService.check(firstVisitCookie, this._localStorage)) {
      this._localStorageService.setItem(
        firstVisitCookie,
        date.toString(),
        0,
        this._localStorage,
      );
    } else {
      date = +(this._localStorageService.getItem(firstVisitCookie, this._localStorage) || 0);
    }

    return new Date(date * 1000);
  }

  private _getRefLocation(ref: string) {
    return (function(r: string) {
      const l = document.createElement('a');
      l.href = r;
      return l;
    })(ref);
  }

  private _isOwnHost(ref: string): boolean {
    const l = this._getRefLocation(ref);
    return !!environment.domainLanguages.find((item) => item.domains.includes(l.hostname));
  }

  /**
   * Удаление куки и local storage
   */
  private _clearCookieAndLocalStorage(key: string): void {
    this._cookieService.removeHttpOnlyItem(key).subscribe(() => this._localStorageService.removeItem(key, this._localStorage));
  }

  /**
   * Удаляем ppu
   */
  public clearPartnerRef(): void {
    this._localStorageService.removeItem(partnerRefKey, this._localStorage);
  }

  /**
   * Удаление cpa
   */
  private _clearCpa(): void {
    this._clearCookieAndLocalStorage(cpaNetworkCookie);
    this._clearCookieAndLocalStorage(cpaUidCookie);
    this._clearCookieAndLocalStorage(cpaUrlCookie);
    this._clearCookieAndLocalStorage(cpaMediumCookie);
  }

  public checkRetargeting(isLoggedId: boolean, params: UtmParams): boolean {

    if (isLoggedId) {
      if (this._localStorageService.getItem(retargetingCookie, this._localStorage)) {
        // TODO api request UserSetRetargetingRequest
        this.clearPartnerRef();
      } else if (params.tracking && params.utm_source === 'facebook') {
        // TODO api request UserSetRetargetingRequest
        this._clearCpa();
        this.clearPartnerRef();

        return true;
      }
    } else if (params.tracking && params.utm_source === 'facebook') {
      this._cookieService.setHttpOnlyItem(
        retargetingCookie,
        params.tracking,
        {
          path: '/',
        },
        environment.partnerCookieExpire,
      ).subscribe(() => this._localStorageService.setItem(
        retargetingCookie, params.tracking, environment.partnerCookieExpire, this._localStorage,
      ));

      this._clearCpa();
      this.clearPartnerRef();

      return true;
    }
    return false;
  }

  /**
   * Проверяем на CPA сети
   * @param {string} referrer
   * @param {UtmParams} params
   * @returns {boolean}
   * @private
   */
  private _checkCpa(referrer: string, params: UtmParams): boolean {
    if (params.utm_source && this._mediumMarkers.includes(params.utm_medium)) {
      const ref = this._referral.find(item => item.name === params.utm_source);

      if (ref) {
        const val = ref;

        const uidKey = val.uid;
        let network = params.utm_source;

        if (typeof ref === 'function') {
          network = val.name;
        }

        if (uidKey in params) {
          this._cookieService.setHttpOnlyItem(
            cpaUidCookie,
            params[uidKey],
            {
              path: '/',
            },
            environment.cpaNetworksCookieExpire,
          ).subscribe(() => this._localStorageService.setItem(
              cpaUidCookie,
              params[uidKey],
              environment.cpaNetworksCookieExpire,
              this._localStorage,
            ),
          );
        } else {
          this._clearCookieAndLocalStorage(cpaUidCookie);
        }

        this._cookieService.setHttpOnlyItem(
          cpaNetworkCookie,
          network,
          {
            path: '/',
          },
          environment.cpaNetworksCookieExpire,
        ).subscribe(() => this._localStorageService.setItem(
          cpaNetworkCookie,
          network,
          environment.cpaNetworksCookieExpire,
          this._localStorage,
        ));

        this._cookieService.setHttpOnlyItem(
          cpaUrlCookie,
          this._location.href,
          {
            expires: new Date(Date.now() - 1000),
            path: '/',
          },
          environment.cpaNetworksCookieExpire,
        ).subscribe(() => this._localStorageService.setItem(
          cpaUrlCookie,
          this._location.href,
          environment.cpaNetworksCookieExpire,
          this._localStorage,
        ));

        this.clearPartnerRef();

        return true;
      } else if (!this._isOwnHost(referrer)) {
        this._clearCpa();
      }
    }
    return false;
  }

  // Проверяем ppu/ppk на возможность установки
  private _checkAndSetPartnerRef(params: ReferralParams): void {
    REFERRAL_QUERY_PARAMS.legacy.forEach((key) => {
      const value = params[key];
      if (value) {
        this._setPartnerRef(key, value);

        return;
      }
    });
  }

  private _setPartnerRef(key: string, value: string): void {
    let ppu = this.getPartnerRefData();

    if (!ppu || !ppu.endsWith(key + '=' + value)) {
      ppu = (ppu ? ppu + ';' : '') + key + '=' + value;
    }
    this._localStorageService.setItem(partnerRefKey, ppu, environment.partnerCookieExpire, this._localStorage);

    this._clearCpa();
  }

  public getPartnerRefData(): string | null {
    return this._localStorageService.getItem(partnerRefKey, this._localStorage);
  }

  public getReferrerName(): string | null {
    return this._localStorageService.getItem(referrerNameKey, this._localStorage);
  }

  public clearReferrerName(): void {
    this._localStorageService.removeItem(referrerNameKey, this._localStorage);
  }

  public resolveRedirectByReferrer(userRole: Role, type: Type, hasReferalData = false, extras?: NavigationExtras): void {
    if (userRole === Role.Partner) {
      this._router.navigate(['/partner']);
      return;
    }

    if (this.getReferrerName() === referrerName && userRole === Role.TradingPlatform) {
      this._router.navigate(['/partner']);
      return;
    } else {
      if (hasReferalData) {
        this._router.navigate([
          userURLs.registrationAccountRoute(type),
        ], extras);
        return;
      } else {
        this._router.navigate(['/profile/verification/express']);
        return;
      }
    }
  }
}
